import { Box, Paper, Skeleton } from "@mui/material";
import {
    useRef,
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useMemo,
} from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import { ExtendedVideoJsPlayer, VideoWithDevice } from "../../../types/Video";
import {
    VideoMetadata,
    ScaledDimensions,
    VideoPlayerOptions,
    VideoPlayerHandler,
} from "../types/VideoTypes";
import { VideoErrorDialog } from "./VideoErrorDialog";
export interface VideoPlayerProps {
    source: string | null;
    video: VideoWithDevice;
    enableDownload?: boolean;
    downloadFilename?: string;
    isDownloading?: boolean;
    options?: Partial<VideoPlayerOptions>;
    className?: string;
    downloadError?: Error | null;
    isFullscreen?: boolean;
    onReady?: (player: ExtendedVideoJsPlayer) => void;
    onMetadataLoaded?: (metadata: VideoMetadata) => void;
    onScaledDimensionsChange?: (dimensions: ScaledDimensions) => void;
    onTimeUpdate?: (time: number) => void;
    onDownload?: () => void;
    onPlayStateChange?: (isPlaying: boolean) => void;
    onVideoElementResize?: (height: number) => void;
    videoElementRef?: React.RefObject<HTMLVideoElement>;
}

export const VideoPlayer = forwardRef<VideoPlayerHandler, VideoPlayerProps>(
    (
        {
            source,
            onReady,
            onMetadataLoaded,
            onScaledDimensionsChange,
            onTimeUpdate,
            onPlayStateChange,
            enableDownload = false,
            downloadFilename = "video.mp4",
            onDownload,
            isDownloading = false,
            isFullscreen = false,
            options = {},
            className = "",
            onVideoElementResize,
            videoElementRef,
        },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const localVideoRef = useRef<HTMLVideoElement>(null);
        const videoRef = videoElementRef || localVideoRef;
        const playerRef = useRef<ExtendedVideoJsPlayer | null>(null);
        const [isPlayerReady, setIsPlayerReady] = useState(false);
        const [videoMetadata, setVideoMetadata] =
            useState<VideoMetadata | null>(null);
        const [videoError, setVideoError] = useState<{ code?: number } | null>(
            null,
        );

        // Stable callback refs
        const onTimeUpdateRef = useRef(onTimeUpdate);
        const onPlayStateChangeRef = useRef(onPlayStateChange);
        const onMetadataLoadedRef = useRef(onMetadataLoaded);
        const onScaledDimensionsChangeRef = useRef(onScaledDimensionsChange);
        const downloadButtonRef = useRef<any>(null);
        const onReadyRef = useRef(onReady);
        const stableOnDownload = useCallback(() => {
            onDownload?.();
        }, [onDownload]);

        useEffect(() => {
            onTimeUpdateRef.current = onTimeUpdate;
            onPlayStateChangeRef.current = onPlayStateChange;
            onMetadataLoadedRef.current = onMetadataLoaded;
            onScaledDimensionsChangeRef.current = onScaledDimensionsChange;
            onReadyRef.current = onReady;
        }, [
            onTimeUpdate,
            onPlayStateChange,
            onMetadataLoaded,
            onScaledDimensionsChange,
            onReady,
        ]);

        const handleCloseErrorDialog = useCallback(() => {
            setVideoError(null);
        }, []);

        const videoJsOptions = useMemo(
            (): VideoPlayerOptions => ({
                fluid: true,
                responsive: false,
                fill: false,
                autoplay: false,
                controls: true,
                preload: "metadata",
                inactivityTimeout: 3000,
                playbackRates: [0.25, 0.5, 0.75, 1, 1.5, 2],
                controlBar: {
                    children: {
                        playToggle: {},
                        progressControl: {},
                        fullscreenToggle: {},
                        playbackRateMenuButton: {},
                    },
                },
                ...options,
            }),
            [options],
        );

        // Track scaled dimensions
        const trackScaledDimensions = useCallback(() => {
            if (!videoRef.current || !videoMetadata) return;

            const rect = videoRef.current.getBoundingClientRect();
            const scaleX = rect.width / videoMetadata.width;
            const scaleY = rect.height / videoMetadata.height;

            onScaledDimensionsChangeRef.current?.({
                width: videoMetadata.width,
                height: videoMetadata.height,
                scaledWidth: Math.floor(rect.width),
                scaledHeight: Math.floor(rect.height),
                scaleX,
                scaleY,
            });
        }, [videoMetadata]);

        // Setup metadata detection
        const setupMetadataDetection = useCallback(
            (player: ExtendedVideoJsPlayer) => {
                const detectDimensions = () => {
                    const width = player.videoWidth();
                    const height = player.videoHeight();

                    if (width > 0 && height > 0) {
                        const aspectRatio = width / height;
                        const aspectRatioString = `${width}:${height}`;
                        const isPortrait = aspectRatio < 0.7;

                        if (isPortrait) {
                            player.addClass("vjs-portrait");
                        } else {
                            player.removeClass("vjs-portrait");
                        }

                        const metadata: VideoMetadata = {
                            aspectRatio,
                            aspectRatioString,
                            isPortrait,
                            width,
                            height,
                        };

                        setVideoMetadata(metadata);
                        onMetadataLoadedRef.current?.(metadata);
                        trackScaledDimensions();
                    }
                };

                player.on("loadedmetadata", detectDimensions);
                player.on("loadeddata", detectDimensions);
                player.on("canplay", detectDimensions);
                player.on("contextmenu", (e: React.MouseEvent) =>
                    e.preventDefault(),
                );
            },
            [trackScaledDimensions],
        );

        // Initialize Video.js player
        useEffect(() => {
            if (!videoRef.current) return;

            // Don't initialize if we're still downloading
            if (isDownloading) return;

            if (!source) return;

            try {
                // Initialize player with or without source
                const player = videojs(
                    videoRef.current,
                    videoJsOptions,
                ) as ExtendedVideoJsPlayer;

                player.ready(() => {
                    if (enableDownload && stableOnDownload) {
                        downloadButtonRef.current = createDownloadButton(
                            player,
                            downloadFilename,
                            stableOnDownload,
                        );
                    }

                    setupMetadataDetection(player);

                    // Time update handler
                    player.on("timeupdate", () => {
                        const time = player.currentTime();
                        if (typeof time === "number") {
                            onTimeUpdateRef.current?.(time);
                        }
                    });

                    // Play state handlers
                    player.on("play", () => {
                        onPlayStateChangeRef.current?.(true);
                    });

                    player.on("pause", () => {
                        onPlayStateChangeRef.current?.(false);
                    });

                    player.on("ended", () => {
                        onPlayStateChangeRef.current?.(false);
                    });

                    // Also trigger on seek
                    player.on("seeked", () => {
                        const time = player.currentTime();
                        if (typeof time === "number") {
                            onTimeUpdateRef.current?.(time);
                        }
                    });

                    // Handle playback errors (e.g., codec issues)
                    player.on("error", () => {
                        const error = player.error();
                        setVideoError(
                            error && error.code == 4
                                ? { code: error.code }
                                : { code: undefined },
                        );
                    });

                    setIsPlayerReady(true);
                    onReadyRef.current?.(player);
                });

                playerRef.current = player;
            } catch (err) {
                console.error("Failed to initialize VideoJS player:", err);
            }

            return () => {
                if (playerRef.current && !playerRef.current.isDisposed()) {
                    try {
                        playerRef.current.dispose();
                    } catch (err) {
                        console.warn("Error disposing player:", err);
                    }
                }
            };
        }, [isDownloading, source]);

        // Handle source updates
        useEffect(() => {
            if (!playerRef.current || !source || playerRef.current.isDisposed())
                return;

            // Update the source when it becomes available
            playerRef.current.src({ src: source, type: "video/mp4" });
            playerRef.current.load();
        }, [source]);

        // Setup ResizeObserver
        useEffect(() => {
            if (!videoRef.current || !isPlayerReady) return;

            const resizeObserver = new ResizeObserver(() => {
                trackScaledDimensions();
                if (onVideoElementResize && videoRef.current) {
                    onVideoElementResize(videoRef.current.offsetHeight);
                }
            });

            resizeObserver.observe(videoRef.current);
            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }

            // Initial size
            if (onVideoElementResize && videoRef.current) {
                onVideoElementResize(videoRef.current.offsetHeight);
            }

            return () => resizeObserver.disconnect();
        }, [isPlayerReady, trackScaledDimensions, onVideoElementResize]);

        // Imperative handle methods
        const seekToTime = useCallback(
            (seconds: number) => {
                if (
                    playerRef.current &&
                    isPlayerReady &&
                    !playerRef.current.isDisposed()
                ) {
                    try {
                        playerRef.current.currentTime(seconds);
                        playerRef.current.play();
                    } catch (err) {
                        console.warn("Error seeking to time:", err);
                    }
                }
            },
            [isPlayerReady],
        );

        const getDimensions = useCallback((): {
            width: number;
            height: number;
        } | null => {
            return videoMetadata
                ? { width: videoMetadata.width, height: videoMetadata.height }
                : null;
        }, [videoMetadata]);

        const getCurrentTime = useCallback((): number | null => {
            if (playerRef.current && !playerRef.current.isDisposed()) {
                const time = playerRef.current.currentTime();
                return typeof time === "number" ? time : null;
            }
            return null;
        }, []);

        const getVideoElement = useCallback((): HTMLVideoElement | null => {
            return videoRef.current;
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                player: playerRef.current,
                seekToTime,
                getDimensions,
                getCurrentTime,
                getVideoElement,
            }),
            [seekToTime, getDimensions, getCurrentTime, getVideoElement],
        );

        const containerAspectRatio = videoMetadata
            ? `${videoMetadata.width} / ${videoMetadata.height}`
            : "16 / 9";

        return (
            <>
                <Paper
                    elevation={1}
                    sx={{
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        backgroundColor: "transparent",
                    }}
                    data-testid="video-canvas"
                >
                    <Box
                        ref={containerRef}
                        className={`video-player-wrapper${
                            videoMetadata?.isPortrait ? " portrait" : ""
                        }${isFullscreen ? " fullscreen" : ""} ${className}`}
                        sx={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isDownloading ? (
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: "800px",
                                    aspectRatio: "16 / 9",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Skeleton
                                    variant="rectangular"
                                    sx={{ width: "100%", height: "100%" }}
                                />
                            </Box>
                        ) : (
                            <Box
                                className={`video-container${
                                    videoMetadata?.isPortrait ? " portrait" : ""
                                }${isFullscreen ? " fullscreen" : ""}`}
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    aspectRatio: containerAspectRatio,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <video
                                    ref={videoRef}
                                    className={`video-js vjs-big-play-centered${
                                        videoMetadata?.isPortrait
                                            ? " portrait"
                                            : ""
                                    }`}
                                    disablePictureInPicture
                                    playsInline
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        display: "block",
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Paper>
                <VideoErrorDialog
                    open={!!videoError}
                    errorCode={videoError?.code}
                    downloadFilename={downloadFilename}
                    onClose={handleCloseErrorDialog}
                    onDownload={onDownload}
                />
            </>
        );
    },
);

VideoPlayer.displayName = "VideoPlayer";

// Download button creation
const createDownloadButton = (
    player: ExtendedVideoJsPlayer,
    filename: string,
    onDownload: () => void,
) => {
    const Button = videojs.getComponent("Button");

    class DownloadButton extends Button {
        constructor(player: ExtendedVideoJsPlayer, options: any = {}) {
            super(player, options);
            (this as any).controlText("Download Video");
            this.addClass("vjs-control vjs-button vjs-download-button");

            const el = this.el() as HTMLElement;
            el.setAttribute("type", "button");
            el.setAttribute("title", `Download`);

            this.createButtonContent();
            (this as any).filename = filename;
            (this as any).onDownload = onDownload;
        }

        createButtonContent() {
            const el = this.el() as HTMLElement;
            let buttonContent = el.querySelector(
                ".vjs-button-content",
            ) as HTMLElement;

            if (!buttonContent) {
                buttonContent = document.createElement("div");
                buttonContent.className = "vjs-button-content";
                el.appendChild(buttonContent);
            }

            buttonContent.innerHTML = `
                <svg viewBox="0 0 24 24" style="width: 18px; height: 18px; fill: currentColor;">
                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                </svg>
            `;
        }

        updateDownloadingState(downloading: boolean) {
            const el = this.el() as HTMLElement;
            const content = el.querySelector(
                ".vjs-button-content",
            ) as HTMLElement;

            if (!content) return;

            if (downloading) {
                content.innerHTML = `
                    <div class="vjs-download-spinner">
                        <div class="vjs-spinner"></div>
                    </div>
                `;
                el.setAttribute("title", "Downloading...");
                (this as any).disable();
            } else {
                this.createButtonContent();
                el.setAttribute("title", `Download ${(this as any).filename}`);
                (this as any).enable();
            }
        }

        handleClick() {
            const onDownload = (this as any).onDownload;
            if (!this.hasClass("vjs-disabled") && onDownload) {
                onDownload();
            }
        }
    }

    videojs.registerComponent("DownloadButton", DownloadButton);

    try {
        const controlBar = player.getChild("controlBar");
        if (controlBar) {
            const downloadButton = new DownloadButton(player, {});
            (controlBar as any).addChild(downloadButton, {}, 10);
            return downloadButton;
        }
    } catch (error) {
        console.warn("Failed to add download button:", error);
    }

    return null;
};
